import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { OrgKind } from '@construction/shared';
import zhCN from '../locales/zh-CN.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import { orgWording } from './org-wording';

/**
 * 界面语言。西班牙语是 CR-055 加的。
 * 再加一种要一起动：`matchLocale`、`LanguageSelect` 的显示名、三个查语言文件的测试。
 */
export const SUPPORTED_LOCALES = ['zh-CN', 'en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'construction.locale';

/**
 * 把任意语言标记（`es-MX`、`zh-Hans-CN`、`en-US`）归到支持的语言之一，认不出来的按英文。
 * 手机系统语言、i18next 当前语言都走这里 —— 各处自己写 `startsWith('zh')` 就会漏掉新加的语言。
 */
export function matchLocale(tag: string): SupportedLocale {
  const lower = tag.toLowerCase();
  if (lower.startsWith('zh')) return 'zh-CN';
  if (lower.startsWith('es')) return 'es';
  return 'en';
}

/**
 * 语言优先级：用户在本机选过的 > 系统语言 > 英文。
 * 登录后如果服务端记着别的语言，会由 auth store 覆盖这里的值 ——
 * 这样工人换手机也不用重新设一次。
 */
function detectLocale(): SupportedLocale {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }
  return matchLocale(navigator.language);
}

/** 原样的语言文件。i18next 合并覆盖时会改它拿到的那个对象，所以给它的是拷贝，这份留着换回去用 */
const BASE = { 'zh-CN': zhCN, en, es } as const;

void i18n.use(initReactI18next).init({
  resources: {
    'zh-CN': { translation: structuredClone(zhCN) },
    en: { translation: structuredClone(en) },
    es: { translation: structuredClone(es) },
  },
  lng: detectLocale(),
  fallbackLng: 'zh-CN',
  interpolation: { escapeValue: false },
  returnNull: false,
});

/**
 * 按部署模式换「公司 / 分公司」「平台 / 总部」的说法（CR-073，理由见 org-wording.ts）。
 * 登录、恢复会话时由 auth store 调，**要在把用户写进 store 之前调** —— 页面按用户渲染，先渲染的就是旧说法。
 */
export function applyOrgKind(kind: OrgKind): void {
  for (const lng of SUPPORTED_LOCALES) {
    i18n.addResourceBundle(lng, 'translation', orgWording(BASE[lng], kind), true, true);
  }
}

export function setLocale(locale: SupportedLocale): void {
  localStorage.setItem(STORAGE_KEY, locale);
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

document.documentElement.lang = i18n.language;

export default i18n;
